import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  OS_IMAGE_CATEGORY_OPTIONS,
  OS_IMAGE_STATUS_OPTIONS,
  OS_IMAGE_TYPE_OPTIONS,
  useOSImageFiltersStore,
  type OSImageListFilters,
} from "@/stores/osImageFiltersStore";
import { Filter, Loader2, Search, X } from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";

const SEARCH_DEBOUNCE_MS = 750;

interface OSImageFiltersProps {
  /**
   * Rendered immediately after the search box, before the active-filter chips — the shared
   * list-screen order puts the screen's own action (here: New OS Image) next to the input.
   */
  actions?: ReactNode;
  /** Rendered after the Filters button, at the end of the same row (the Columns menu). */
  trailing?: ReactNode;
}

/** One removable chip for a filter that differs from its default. */
function FilterChip({
  label,
  onClear,
}: {
  label: string;
  onClear: () => void;
}) {
  return (
    <Badge
      variant="outline"
      className="shrink-0 gap-1 whitespace-nowrap border-primary/20 bg-primary/10 text-primary"
    >
      {label}
      <button
        type="button"
        onClick={onClear}
        aria-label={`Clear ${label}`}
        className="cursor-pointer rounded-sm hover:text-destructive focus:outline-none"
      >
        <X className="h-3 w-3" />
      </button>
    </Badge>
  );
}

const labelOf = <T,>(options: { value: T; label: string }[], value: T) =>
  options.find((option) => option.value === value)?.label ?? String(value);

/**
 * The OS image list toolbar, laid out like the Resources list: one row of search box, create
 * action, active-filter chips, Filters popover and Columns menu. Behaviour follows UserFilters —
 * see there for why each guard exists.
 */
export function OSImageFilters({ actions, trailing }: OSImageFiltersProps) {
  const filters = useOSImageFiltersStore((state) => state.filters);
  const setFilter = useOSImageFiltersStore((state) => state.setFilter);
  const resetCount = useOSImageFiltersStore((state) => state.resetCount);
  const [search, setSearchInput] = useState(filters.search || "");

  // Tracks the last value this component pushed into the store, so a change made elsewhere
  // (a chip's clear button) is mirrored into the box without echoing the box's own writes.
  const lastPushedSearch = useRef(filters.search || "");

  useEffect(() => {
    const storeSearch = filters.search || "";

    if (storeSearch !== lastPushedSearch.current) {
      lastPushedSearch.current = storeSearch;
      setSearchInput(storeSearch);
    }
  }, [filters.search]);

  // A reset that lands while a term is still uncommitted changes nothing the effect above can see;
  // `resetCount` is the signal. The first run is skipped so a term restored from sessionStorage is
  // not wiped on mount.
  const seenResetCount = useRef(resetCount);
  useEffect(() => {
    if (seenResetCount.current === resetCount) {
      return;
    }

    seenResetCount.current = resetCount;
    setSearchInput("");
    lastPushedSearch.current = "";
  }, [resetCount]);

  useEffect(() => {
    const trimmed = search.trim();

    if (trimmed === (filters.search || "")) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      lastPushedSearch.current = trimmed;
      setFilter("search", trimmed);
    }, SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timeoutId);
  }, [filters.search, search, setFilter]);

  const isSearchPending = search.trim() !== (filters.search || "");

  const selectFilter = <K extends "category" | "osType" | "status">(
    id: string,
    label: string,
    key: K,
    options: { value: OSImageListFilters[K]; label: string }[],
  ) => (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <Select
        value={String(filters[key])}
        onValueChange={(value) =>
          setFilter(key, value as OSImageListFilters[K])
        }
      >
        <SelectTrigger id={id}>
          <SelectValue placeholder={label} />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={String(option.value)} value={String(option.value)}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-3 sm:flex-nowrap sm:items-end">
      {/* Search Input */}
      <div className="w-full sm:w-90">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          {/* The debounce window has no isFetching to report, so the box says so itself. */}
          {isSearchPending && (
            <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
          )}
          <Input
            placeholder="Search name, template or description"
            value={search}
            onChange={(e) => setSearchInput(e.target.value)}
            // React withholds onChange while an IME composition is open (Vietnamese Telex/VNI).
            onCompositionEnd={(e) => setSearchInput(e.currentTarget.value)}
            className="pl-10 pr-9"
          />
        </div>
      </div>

      {actions}

      {/* Active Filter Badges */}
      <div className="order-3 flex w-full min-w-0 items-center justify-start gap-2 overflow-x-auto sm:order-0 sm:w-auto sm:flex-1 sm:self-center sm:justify-end">
        {filters.search && (
          <FilterChip
            label={`Search: ${filters.search}`}
            onClear={() => {
              setSearchInput("");
              setFilter("search", "");
            }}
          />
        )}
        {filters.category !== "All" && (
          <FilterChip
            label={`Category: ${labelOf(OS_IMAGE_CATEGORY_OPTIONS, filters.category)}`}
            onClear={() => setFilter("category", "All")}
          />
        )}
        {filters.osType !== "All" && (
          <FilterChip
            label={`OS Type: ${labelOf(OS_IMAGE_TYPE_OPTIONS, filters.osType)}`}
            onClear={() => setFilter("osType", "All")}
          />
        )}
        {filters.status !== "All" && (
          <FilterChip
            label={`Status: ${filters.status}`}
            onClear={() => setFilter("status", "All")}
          />
        )}
      </div>

      <Popover>
        {/* Icon only: `title` gives the hover tooltip, `aria-label` the accessible name. */}
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            title="Filters"
            aria-label="Filters"
            className="h-8 min-h-8 w-8 shrink-0 p-0 hover:translate-y-0 active:translate-y-0"
          >
            <Filter className="h-4 w-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-80 space-y-4 p-4">
          {selectFilter(
            "osImageCategoryFilter",
            "Category",
            "category",
            OS_IMAGE_CATEGORY_OPTIONS,
          )}
          {selectFilter(
            "osImageTypeFilter",
            "OS Type",
            "osType",
            OS_IMAGE_TYPE_OPTIONS,
          )}
          {selectFilter(
            "osImageStatusFilter",
            "Status",
            "status",
            OS_IMAGE_STATUS_OPTIONS,
          )}
        </PopoverContent>
      </Popover>

      {trailing}
    </div>
  );
}
